import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { useCssHandles } from '../hooks/useCssHandles'
import { Layout } from '../shared/components'
import { brandService } from '../services'
import type { Brand } from '../services/brandService'
import { buildImageUrl } from '../config'

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`

const MarqueeContainer = styled.div`
  overflow: hidden;
  width: 100%;
  position: relative;
  
  &::before, &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 100px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(to right, #f9fafb, transparent);
  }
  
  &::after {
    right: 0;
    background: linear-gradient(to left, #f9fafb, transparent);
  }
`

const MarqueeTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${scroll} 40s linear infinite;
  padding: 1rem 0;
  
  &:hover {
    animation-play-state: paused;
  }
`

const CSS_HANDLES = [
  'brands',
  'hero',
  'heroContent',
  'heroTitle',
  'heroSubtitle',
  'heroImage',
  'brandsGrid',
  'brandCard',
  'brandLogo',
  'brandName',
  'brandDescription',
  'brandProducts',
  'filterSection',
  'filterTitle',
  'filterButtons',
  'filterButton',
  'filterButtonActive',
  'statsSection',
  'statsGrid',
  'statCard',
  'statIcon',
  'statNumber',
  'statLabel',
  'ctaSection',
  'ctaContent',
  'ctaTitle',
  'ctaButton'
] as const

const Brands: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const [brands, setBrands] = useState<Brand[]>([])
  const [brandsLoading, setBrandsLoading] = useState(true)

  // Carregar marcas da API
  useEffect(() => {
    const loadBrands = async () => {
      try {
        setBrandsLoading(true)
        const response = await brandService.list({ limit: 100 })
        setBrands(response.brands)
      } catch (err) {
        console.error('Erro ao carregar marcas:', err)
        setBrands([])
      } finally {
        setBrandsLoading(false)
      }
    }

    void loadBrands()
  }, [])

  // Para featured, vamos pegar as primeiras 3 marcas ordenadas por sortOrder
  const featuredBrands = brands
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .slice(0, 3)

  return (
    <Layout>
      <div className={`${handles.brands}`}>
        {/* Hero Section */}
        <section className={`${handles.hero} relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 text-white overflow-hidden`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-24">
            <div className="grid grid-cols-1 gap-16 items-center">
              <div className={`${handles.heroContent} text-center lg:text-left`}>
                <div className="mb-6">
                  <span className="inline-block bg-yellow-500 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                    🏷️ Marcas de Baterias
                  </span>
                </div>
                
                <h1 className={`${handles.heroTitle} text-5xl lg:text-6xl font-bold mb-6 leading-tight`}>
                  As Melhores <span className="text-yellow-400">Marcas</span> de Baterias
                </h1>
                
                <p className={`${handles.heroSubtitle} text-xl mb-8 text-blue-100 leading-relaxed`}>
                  Conheça as principais marcas de baterias automotivas disponíveis em nossa loja. 
                  Qualidade, confiabilidade e garantia em cada produto.
                </p>

                <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                  <Link
                    to="/catalogo"
                    className="bg-yellow-500 text-blue-900 px-8 py-4 rounded-xl font-semibold hover:bg-yellow-400 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Ver Catálogo Completo
                  </Link>
                  <Link
                    to="/sobre"
                    className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-900 transition-all duration-300"
                  >
                    Sobre Nós
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className={`${handles.statsSection} py-16 bg-gray-50`}>
          <div className="max-w-7xl mx-auto px-4">
            <div className={`${handles.statsGrid} grid grid-cols-1 md:grid-cols-4 gap-8`}>
              <div className={`${handles.statCard} text-center`}>
                <div className={`${handles.statIcon} text-4xl mb-4`}>🏷️</div>
                <div className={`${handles.statNumber} text-3xl font-bold text-blue-600 mb-2`}>
                  {brands.length}
                </div>
                <div className={`${handles.statLabel} text-gray-600`}>
                  Marcas Disponíveis
                </div>
              </div>
              
              <div className={`${handles.statCard} text-center`}>
                <div className={`${handles.statIcon} text-4xl mb-4`}>🔋</div>
                <div className={`${handles.statNumber} text-3xl font-bold text-blue-600 mb-2`}>
                  {brands.length > 0 ? `${brands.length * 10}+` : '0'}
                </div>
                <div className={`${handles.statLabel} text-gray-600`}>
                  Produtos no Catálogo
                </div>
              </div>
              
              <div className={`${handles.statCard} text-center`}>
                <div className={`${handles.statIcon} text-4xl mb-4`}>🌍</div>
                <div className={`${handles.statNumber} text-3xl font-bold text-blue-600 mb-2`}>
                  4
                </div>
                <div className={`${handles.statLabel} text-gray-600`}>
                  Países de Origem
                </div>
              </div>
              
              <div className={`${handles.statCard} text-center`}>
                <div className={`${handles.statIcon} text-4xl mb-4`}>⭐</div>
                <div className={`${handles.statNumber} text-3xl font-bold text-blue-600 mb-2`}>
                  15+
                </div>
                <div className={`${handles.statLabel} text-gray-600`}>
                  Anos de Experiência
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Brands */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Marcas <span className="text-blue-600">Destaque</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Conheça nossas principais marcas parceiras, reconhecidas mundialmente pela qualidade e confiabilidade
              </p>
            </div>
            
            {brandsLoading ? (
              <div className={`${handles.brandsGrid} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`}>
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="bg-white p-8 rounded-2xl shadow-lg space-y-4">
                    <div className="h-24 w-full rounded bg-gray-200 animate-pulse" />
                    <div className="h-6 w-3/4 rounded bg-gray-200 animate-pulse mx-auto" />
                    <div className="h-4 w-full rounded bg-gray-200 animate-pulse" />
                    <div className="h-10 w-full rounded bg-gray-200 animate-pulse" />
                  </div>
                ))}
              </div>
            ) : (
              <div className={`${handles.brandsGrid} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`}>
                {featuredBrands.length > 0 ? (
                  featuredBrands.map((brand) => {
                  const logoPath = `/uploads/brands/${brand.slug}.webp`
                    const logoUrl = logoPath.startsWith('http')
                      ? logoPath
                      : buildImageUrl(logoPath)!
                    
                    return (
                      <div
                        key={brand.id}
                        className={`${handles.brandCard} bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100`}
                      >
                        <div className="text-center mb-6">
                          <div className={`${handles.brandLogo} mb-4 flex justify-center items-center h-24`}>
                            <img src={logoUrl} alt={brand.name} className="max-h-full max-w-full object-contain" />
                          </div>
                          <h3 className={`${handles.brandName} text-2xl font-bold text-gray-900 mb-2`}>
                            {brand.name}
                          </h3>
                        </div>
                        
                        {brand.description && (
                          <p className={`${handles.brandDescription} text-gray-600 mb-6 leading-relaxed`}>
                            {brand.description}
                          </p>
                        )}
                        
                        <Link
                          to={`/catalogo?marca=${brand.name.toLowerCase()}`}
                          className="block w-full bg-blue-600 text-white text-center py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-300"
                        >
                          Ver Produtos {brand.name}
                        </Link>
                      </div>
                    )
                  })
                ) : (
                  <p className="text-gray-500 col-span-3 text-center">Nenhuma marca disponível</p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Filter Section - Now Infinite Carousel */}
        <section className="py-16 bg-gray-50 overflow-hidden">
          <div className="w-full">
            <div className="text-center mb-12 px-4">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Todas as <span className="text-blue-600">Marcas</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Explore nossa seleção completa de marcas de baterias
              </p>
            </div>

            {/* All Brands Infinite Carousel */}
            {brandsLoading ? (
              <div className="max-w-7xl mx-auto px-4">
                <div className={`${handles.brandsGrid} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6`}>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="bg-white p-6 rounded-xl shadow-md space-y-3">
                      <div className="h-16 w-full rounded bg-gray-200 animate-pulse" />
                      <div className="h-4 w-3/4 rounded bg-gray-200 animate-pulse mx-auto" />
                      <div className="h-4 w-full rounded bg-gray-200 animate-pulse" />
                      <div className="h-8 w-full rounded bg-gray-200 animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="w-full">
                {brands.length > 0 ? (
                  <MarqueeContainer>
                    <MarqueeTrack>
                      {[...brands, ...brands].map((brand, index) => {
                        const logoPath = `/uploads/brands/${brand.slug}.webp`
                        const logoUrl = logoPath.startsWith('http')
                          ? logoPath
                          : buildImageUrl(logoPath)!
                        
                        return (
                          <Link
                            key={`${brand.id}-${index}`}
                            to={`/catalogo?marca=${brand.name.toLowerCase()}`}
                            className={`${handles.brandCard} bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 mx-4 w-48 h-32 flex items-center justify-center flex-shrink-0 group`}
                          >
                            <div className={`${handles.brandLogo} flex justify-center items-center w-full h-full p-2`}>
                              <img 
                                src={logoUrl} 
                                alt={brand.name} 
                                className="max-h-full max-w-full object-contain filter grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300" 
                              />
                            </div>
                          </Link>
                        )
                      })}
                    </MarqueeTrack>
                  </MarqueeContainer>
                ) : (
                  <p className="text-gray-500 text-center px-4">Nenhuma marca encontrada</p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className={`${handles.ctaSection} py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white`}>
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className={`${handles.ctaContent}`}>
              <h2 className={`${handles.ctaTitle} text-4xl font-bold mb-6`}>
                Não encontrou a marca que procura?
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Entre em contato conosco! Trabalhamos com as principais marcas do mercado 
                e podemos ajudar você a encontrar exatamente o que precisa.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/catalogo"
                  className="bg-yellow-500 text-blue-900 px-8 py-4 rounded-xl font-semibold hover:bg-yellow-400 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Ver Catálogo Completo
                </Link>
                <a
                  href="https://wa.me/551932552356?text=Olá!%20Gostaria%20de%20falar%20com%20um%20especialista%20sobre%20marcas%20de%20baterias."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-900 transition-all duration-300"
                >
                  Falar com Especialista
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default Brands
